import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PublicationsApi from "../../api/publicationsApi";
import Input from "../../components/Input/Input";
import MultipleSelect from "../../components/MultipleSelect/MultipleSelect";
import { Button } from "../../components/ui/button";
import {
  Publication,
  PublicationFilterScope,
  PublicationSort,
} from "../../types/Publications";
import PublicationArtwork from "./PublicationArtwork";
import PublicationInspector from "./PublicationInspector";
import {
  formatRequestDate,
  getFallbackTotal,
  getHubLabel,
  getLifecycleFilters,
  getMaterialsLabel,
  getPublicationLifecycle,
  getPublisher,
  getRelevantTimestamp,
  getUserLabel,
  PublicationLifecycle,
} from "./publicationQueueModel";
import s from "./Publications.module.css";

const DEFAULT_PAGE_SIZE = 10;
const SEARCH_DELAY_MS = 300;

const lifecycleOptions: { value: PublicationLifecycle; label: string }[] = [
  { value: "ALL", label: "All lifecycles" },
  { value: "DRAFT", label: "Draft" },
  { value: "UNDER_REVIEW", label: "Under review" },
  { value: "CHANGES_REQUESTED", label: "Changes requested" },
  { value: "APPROVED", label: "Approved" },
  { value: "ACTIVE", label: "Active" },
  { value: "DELETED", label: "Deleted" },
];

const sortOptions: { value: PublicationSort; label: string }[] = [
  { value: PublicationSort.NEWEST, label: "Newest first" },
  { value: PublicationSort.TITLE_ASC, label: "Title A–Z" },
  { value: PublicationSort.TITLE_DESC, label: "Title Z–A" },
  {
    value: PublicationSort.APPROVE_REQUESTED_AT_DESC,
    label: "Latest review request",
  },
];

const AllPublications = ({ token }: { token: string | null }) => {
  const [publications, setPublications] = useState<Publication[]>([]);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [lifecycle, setLifecycle] = useState<PublicationLifecycle>("ALL");
  const [sort, setSort] = useState(PublicationSort.NEWEST);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [total, setTotal] = useState(0);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [retryVersion, setRetryVersion] = useState(0);
  const requestIdRef = useRef(0);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedSearch(search.trim());
      setPage(0);
      setSelectedId(null);
    }, SEARCH_DELAY_MS);
    return () => window.clearTimeout(timeoutId);
  }, [search]);

  const lifecycleFilters = useMemo(
    () => getLifecycleFilters(lifecycle),
    [lifecycle],
  );

  const loadHistory = useCallback(async () => {
    const requestId = ++requestIdRef.current;
    if (!token) {
      setPublications([]);
      setTotal(0);
      setSelectedId(null);
      setError(
        "Your admin session is unavailable. Sign in again to load Publications.",
      );
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      const response = await PublicationsApi.getFilteredPublications(token, {
        scope: PublicationFilterScope.ALL,
        page: page + 1,
        limit: pageSize,
        ...(debouncedSearch ? { text: debouncedSearch } : {}),
        ...lifecycleFilters,
        sort,
      });
      if (!response.status) {
        throw new Error(
          response.message || "Unable to load Publication history.",
        );
      }
      if (requestId !== requestIdRef.current) return;

      const nextPublications = response.publications ?? [];
      setPublications(nextPublications);
      setTotal(
        getFallbackTotal(
          page,
          pageSize,
          nextPublications.length,
          response.totalAmount,
        ),
      );
      setSelectedId((current) =>
        nextPublications.some(({ _id }) => _id === current) ? current : null,
      );
    } catch (loadError) {
      if (requestId !== requestIdRef.current) return;
      console.error("Error loading Publication history:", loadError);
      setPublications([]);
      setTotal(0);
      setSelectedId(null);
      setError(
        loadError instanceof Error
          ? loadError.message
          : "Unable to load Publication history.",
      );
    } finally {
      if (requestId === requestIdRef.current) setIsLoading(false);
    }
  }, [debouncedSearch, lifecycleFilters, page, pageSize, sort, token]);

  useEffect(() => {
    loadHistory();
    return () => {
      requestIdRef.current += 1;
    };
  }, [loadHistory, retryVersion]);

  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const resetCriteria = () => {
    setSearch("");
    setDebouncedSearch("");
    setLifecycle("ALL");
    setSort(PublicationSort.NEWEST);
    setPage(0);
    setSelectedId(null);
  };

  return (
    <div className={s.historyWorkspace}>
      <div className={s.historyColumn}>
        <div
          className={s.historyToolbar}
          aria-label="Publication history filters"
        >
          {/* <label className={s.searchField}>
            <span>Search Publications</span> */}
          <Input
            inputValue={search}
            onChangeInput={setSearch}
            type="search"
            placeholder="Title or Publication ID"
            required={false}
            isSearchinput
            ariaLabel="Search Publications"
          />
          {/*    </label> */}

          <MultipleSelect
            data={lifecycleOptions.map(({ value, label }) => ({
              value,
              lable: label,
            }))}
            value={lifecycle}
            setValue={(value) => {
              setLifecycle(value as PublicationLifecycle);
              setPage(0);
              setSelectedId(null);
            }}
            multiplie={false}
            lable="Lifecycle"
            ariaLabel="Lifecycle"
          />

          <MultipleSelect
            data={sortOptions.map(({ value, label }) => ({
              value,
              lable: label,
            }))}
            value={sort}
            setValue={(value) => {
              setSort(value as PublicationSort);
              setPage(0);
              setSelectedId(null);
            }}
            multiplie={false}
            lable="Sort by"
            ariaLabel="Sort by"
          />

          <Button
            type="button"
            variant="outline"
            onClick={resetCriteria}
            disabled={isLoading && !error}
          >
            Clear filters
          </Button>
        </div>

        <div className={s.historySummary} aria-live="polite">
          <strong>
            {total} {total === 1 ? "Publication" : "Publications"}
          </strong>
          <span>Complete administrative scope, including deleted records</span>
        </div>

        {isLoading ? (
          <div className={s.state} role="status">
            Loading all Publications…
          </div>
        ) : error ? (
          <div className={s.state} role="alert">
            <strong>Publication history could not be loaded</strong>
            <span>{error}</span>
            <Button
              type="button"
              onClick={() => setRetryVersion((value) => value + 1)}
            >
              <RefreshIcon aria-hidden="true" /> Retry
            </Button>
          </div>
        ) : publications.length === 0 ? (
          <div className={s.state} role="status">
            <strong>No Publications found</strong>
            <span>Try a different search or lifecycle filter.</span>
            {(debouncedSearch || lifecycle !== "ALL") && (
              <Button type="button" variant="outline" onClick={resetCriteria}>
                Clear filters
              </Button>
            )}
          </div>
        ) : (
          <div className={s.historyTableWrap}>
            <table className={s.historyTable}>
              <caption className={s.visuallyHidden}>
                All Publications audit results
              </caption>
              <thead>
                <tr>
                  <th scope="col">Publication</th>
                  <th scope="col">Lifecycle</th>
                  <th scope="col">Creator / Publisher</th>
                  <th scope="col">Kind / Language</th>
                  <th scope="col">Formats</th>
                  <th scope="col">Relevant date</th>
                </tr>
              </thead>
              <tbody>
                {publications.map((publication) => {
                  const publisher = getPublisher(publication);
                  const lifecycleLabel = getPublicationLifecycle(publication);
                  const timestamp = getRelevantTimestamp(publication);
                  const isSelected = selectedId === publication._id;
                  return (
                    <tr
                      key={publication._id}
                      className={isSelected ? s.selectedHistoryRow : undefined}
                    >
                      <td data-label="Publication">
                        <div className={s.historyIdentity}>
                          <PublicationArtwork
                            logoUrl={publication.logoUrl}
                            kind={publication.kind}
                            title={publication.title}
                          />
                          <button
                            type="button"
                            aria-pressed={isSelected}
                            aria-label={`Inspect ${publication.title || "Untitled Publication"}`}
                            onClick={() => setSelectedId(publication._id)}
                          >
                            <strong>
                              {publication.title || "Untitled Publication"}
                            </strong>
                            <span>{publication.shortId}</span>
                          </button>
                        </div>
                      </td>
                      <td data-label="Lifecycle">
                        <span
                          className={s.lifecycleBadge}
                          data-lifecycle={lifecycleLabel}
                        >
                          {lifecycleLabel}
                        </span>
                      </td>
                      <td
                        data-label="Creator / Publisher"
                        className={s.historyPeople}
                      >
                        <span>
                          <b>Creator</b>
                          {getUserLabel(publication.createdBy)}
                        </span>
                        <span>
                          <b>Publisher</b>
                          {publisher.type} · {publisher.name}
                        </span>
                        {publication.educationalHub && (
                          <span>
                            <b>Hub</b>
                            {getHubLabel(publication.educationalHub)}
                          </span>
                        )}
                      </td>
                      <td data-label="Kind / Language">
                        {publication.kind ?? "Not set"} ·{" "}
                        {publication.language.toUpperCase()}
                      </td>
                      <td data-label="Formats">
                        {getMaterialsLabel(publication)}
                      </td>
                      <td data-label="Relevant date">
                        <span className={s.historyDate}>
                          <b>{timestamp.label}</b>
                          {formatRequestDate(timestamp.value)}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <footer
          className={s.pagination}
          aria-label="All Publications pagination"
        >
          <div className={s.pageSizeField}>
            <MultipleSelect
              data={[10, 20, 50].map((size) => ({
                value: String(size),
                lable: String(size),
              }))}
              value={String(pageSize)}
              setValue={(value) => {
                setPageSize(Number(value));
                setPage(0);
                setSelectedId(null);
              }}
              multiplie={false}
              disabled={isLoading}
              lable="Rows per page"
            />
          </div>
          <span>
            Page {page + 1} of {pageCount}
          </span>
          <div className={s.paginationActions}>
            <Button
              type="button"
              variant="outline"
              aria-label="Previous page"
              disabled={isLoading || page === 0}
              onClick={() => {
                setSelectedId(null);
                setPage((value) => Math.max(0, value - 1));
              }}
            >
              <NavigateBeforeIcon aria-hidden="true" />
            </Button>
            <Button
              type="button"
              variant="outline"
              aria-label="Next page"
              disabled={isLoading || page + 1 >= pageCount}
              onClick={() => {
                setSelectedId(null);
                setPage((value) => value + 1);
              }}
            >
              <NavigateNextIcon aria-hidden="true" />
            </Button>
          </div>
        </footer>
      </div>

      <div
        className={`${s.historyInspector} ${selectedId ? s.historyInspectorActive : ""}`}
        aria-hidden={!selectedId}
      >
        {selectedId && (
          <>
            <div className={s.historyInspectorHeader}>
              <span className={s.historyInspectorLabel}>
                Publication details
              </span>
              <button
                type="button"
                className={s.historyInspectorClose}
                onClick={() => setSelectedId(null)}
              >
                Hide
              </button>
            </div>
            <PublicationInspector
              publicationId={selectedId}
              token={token}
              readOnly
            />
          </>
        )}
      </div>
    </div>
  );
};

export default AllPublications;
